import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios"

import { getApiBase } from "utils/server"

/**
 * Creates an authenticated axios instance with automatic header injection
 *
 * @param token - Authentication token
 * @param user - Username
 * @param unit - User unit
 * @param role - User role
 * @param baseURL - API base URL; defaults to the current origin's API.
 *                  Pass a serverApiBase() for cross-server calls (superadmin).
 * @returns Configured axios instance
 */
const createAuthenticatedAxios = (
  token: string,
  user: string,
  unit: string,
  role: string,
  baseURL: string = getApiBase()
): AxiosInstance => {
  const instance: AxiosInstance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json", // Global default, just like your original
    },
  })

  // Add request interceptor to automatically include auth headers
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      // Add authentication headers (individual API calls can still override these)
      config.headers.set("token", token)
      config.headers.set("user", user)
      config.headers.set("unit", unit)
      config.headers.set("role", role)

      return config
    },
    (error: AxiosError): Promise<AxiosError> => {
      return Promise.reject(error)
    }
  )

  // Add response interceptor for centralized error handling
  instance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => response,
    (error: AxiosError): Promise<AxiosError> => {
      // Backstop for the server check: the conductor rejects normal/admin users
      // whose organization lives on another server. A full reload re-runs the
      // ServerGuard, which redirects to the correct server subdomain. The
      // sessionStorage flag prevents a reload storm if guard and backend disagree.
      const data = error.response?.data
      if (
        error.response?.status === 403 &&
        typeof data === "string" &&
        data.includes("Wrong server") &&
        !sessionStorage.getItem("dq_403_handled")
      ) {
        sessionStorage.setItem("dq_403_handled", "1")
        window.location.assign("/home")
      }
      console.error("API Error:", error)
      // You can add your ErrorNotification here if needed
      // ErrorNotification(error.name, error.message)
      return Promise.reject(error)
    }
  )

  return instance
}

export default createAuthenticatedAxios
